'use client';

import { createClient } from '@/lib/supabase/client';

export type CampaignStatus = 'draft' | 'active' | 'paused' | 'archived';

export interface Campaign {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  bannerUrl: string | null;
  bannerPath: string | null;
  registrationUrl: string | null;
  whatsappMessage: string | null;
  campaignStatus: CampaignStatus;
  startDate: string | null;
  endDate: string | null;
  qrScans: number;
  uniqueScans: number;
  shareInitiated: number;
  createdAt: string;
  updatedAt: string;
}

export interface BannerVersion {
  id: string;
  campaignId: string;
  bannerUrl: string;
  bannerPath: string | null;
  versionNumber: number;
  isCurrent: boolean;
  uploadedAt: string;
}

export interface CampaignAnalyticsEvent {
  campaignId: string;
  eventType: 'scan' | 'share_initiated';
  deviceType?: string;
  browser?: string;
  country?: string;
  region?: string;
}

function rowToCampaign(row: any): Campaign {
  return {
    id: row.id,
    name: row.name,
    slug: row.slug,
    description: row.description,
    bannerUrl: row.banner_url,
    bannerPath: row.banner_path,
    registrationUrl: row.registration_url,
    whatsappMessage: row.whatsapp_message,
    campaignStatus: row.campaign_status,
    startDate: row.start_date,
    endDate: row.end_date,
    qrScans: row.qr_scans ?? 0,
    uniqueScans: row.unique_scans ?? 0,
    shareInitiated: row.share_initiated ?? 0,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

export const campaignService = {
  async getAll(): Promise<Campaign[]> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('campaigns')
      .select('*')
      .order('created_at', { ascending: false });
    if (error) { console.error('getAll campaigns error:', error.message); return []; }
    return (data || []).map(rowToCampaign);
  },

  async getBySlug(slug: string): Promise<Campaign | null> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('campaigns')
      .select('*')
      .eq('slug', slug)
      .maybeSingle();
    if (error) { console.error('getBySlug error:', error.message); return null; }
    return data ? rowToCampaign(data) : null;
  },

  async getById(id: string): Promise<Campaign | null> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('campaigns')
      .select('*')
      .eq('id', id)
      .maybeSingle();
    if (error) { console.error('getById error:', error.message); return null; }
    return data ? rowToCampaign(data) : null;
  },

  async create(input: {
    name: string;
    slug: string;
    description?: string;
    bannerUrl?: string;
    bannerPath?: string;
    registrationUrl?: string;
    whatsappMessage?: string;
    campaignStatus?: CampaignStatus;
    startDate?: string;
    endDate?: string;
  }): Promise<Campaign | null> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('campaigns')
      .insert({
        name: input.name,
        slug: input.slug,
        description: input.description || null,
        banner_url: input.bannerUrl || null,
        banner_path: input.bannerPath || null,
        registration_url: input.registrationUrl || null,
        whatsapp_message: input.whatsappMessage || null,
        campaign_status: input.campaignStatus || 'draft',
        start_date: input.startDate || null,
        end_date: input.endDate || null,
      })
      .select()
      .single();
    if (error) { console.error('create campaign error:', error.message); return null; }
    return rowToCampaign(data);
  },

  async update(id: string, input: Partial<{
    name: string;
    slug: string;
    description: string;
    bannerUrl: string;
    bannerPath: string;
    registrationUrl: string;
    whatsappMessage: string;
    campaignStatus: CampaignStatus;
    startDate: string;
    endDate: string;
  }>): Promise<Campaign | null> {
    const supabase = createClient();
    const updateData: any = {};
    if (input.name !== undefined) updateData.name = input.name;
    if (input.slug !== undefined) updateData.slug = input.slug;
    if (input.description !== undefined) updateData.description = input.description;
    if (input.bannerUrl !== undefined) updateData.banner_url = input.bannerUrl;
    if (input.bannerPath !== undefined) updateData.banner_path = input.bannerPath;
    if (input.registrationUrl !== undefined) updateData.registration_url = input.registrationUrl;
    if (input.whatsappMessage !== undefined) updateData.whatsapp_message = input.whatsappMessage;
    if (input.campaignStatus !== undefined) updateData.campaign_status = input.campaignStatus;
    if (input.startDate !== undefined) updateData.start_date = input.startDate;
    if (input.endDate !== undefined) updateData.end_date = input.endDate;

    const { data, error } = await supabase
      .from('campaigns')
      .update(updateData)
      .eq('id', id)
      .select()
      .single();
    if (error) { console.error('update campaign error:', error.message); return null; }
    return rowToCampaign(data);
  },

  async updateStatus(id: string, status: CampaignStatus): Promise<boolean> {
    const supabase = createClient();
    const { error } = await supabase
      .from('campaigns')
      .update({ campaign_status: status })
      .eq('id', id);
    if (error) { console.error('updateStatus error:', error.message); return false; }
    return true;
  },

  async delete(id: string): Promise<boolean> {
    const supabase = createClient();
    const { error } = await supabase.from('campaigns').delete().eq('id', id);
    if (error) { console.error('delete campaign error:', error.message); return false; }
    return true;
  },

  async duplicate(id: string): Promise<Campaign | null> {
    const original = await campaignService.getById(id);
    if (!original) return null;
    const baseSlug = original.slug + '-copy';
    let slug = baseSlug;
    let counter = 1;
    // Find unique slug
    while (true) {
      const existing = await campaignService.getBySlug(slug);
      if (!existing) break;
      slug = `${baseSlug}-${counter}`;
      counter++;
    }
    return campaignService.create({
      name: original.name + ' (Copy)',
      slug,
      description: original.description || undefined,
      registrationUrl: original.registrationUrl || undefined,
      whatsappMessage: original.whatsappMessage || undefined,
      campaignStatus: 'draft',
      startDate: original.startDate || undefined,
      endDate: original.endDate || undefined,
    });
  },

  async uploadBanner(campaignId: string, file: File): Promise<{ url: string; path: string } | null> {
    const supabase = createClient();
    const ext = file.name.split('.').pop()?.toLowerCase() || 'jpg';
    const path = `campaigns/${campaignId}/banner-${Date.now()}.${ext}`;
    const { error: uploadError } = await supabase.storage
      .from('campaign-banners')
      .upload(path, file, { upsert: true, cacheControl: '3600' });
    if (uploadError) { console.error('uploadBanner error:', uploadError.message); return null; }
    const { data: { publicUrl } } = supabase.storage.from('campaign-banners').getPublicUrl(path);
    return { url: publicUrl, path };
  },

  async replaceBanner(campaignId: string, file: File): Promise<boolean> {
    const supabase = createClient();
    const campaign = await campaignService.getById(campaignId);
    if (!campaign) return false;

    const uploaded = await campaignService.uploadBanner(campaignId, file);
    if (!uploaded) return false;

    // Get current version number
    const { data: versions } = await supabase
      .from('banner_versions')
      .select('version_number')
      .eq('campaign_id', campaignId)
      .order('version_number', { ascending: false })
      .limit(1);
    const nextVersion = versions && versions.length > 0 ? versions[0].version_number + 1 : 1;

    // Mark old versions as not current
    await supabase
      .from('banner_versions')
      .update({ is_current: false })
      .eq('campaign_id', campaignId);

    // Insert new version
    await supabase.from('banner_versions').insert({
      campaign_id: campaignId,
      banner_url: uploaded.url,
      banner_path: uploaded.path,
      version_number: nextVersion,
      is_current: true,
    });

    // Update campaign banner_url
    await campaignService.update(campaignId, { bannerUrl: uploaded.url, bannerPath: uploaded.path });
    return true;
  },

  async getBannerVersions(campaignId: string): Promise<BannerVersion[]> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('banner_versions')
      .select('*')
      .eq('campaign_id', campaignId)
      .order('version_number', { ascending: false });
    if (error) { console.error('getBannerVersions error:', error.message); return []; }
    return (data || []).map((row: any) => ({
      id: row.id,
      campaignId: row.campaign_id,
      bannerUrl: row.banner_url,
      bannerPath: row.banner_path,
      versionNumber: row.version_number,
      isCurrent: row.is_current,
      uploadedAt: row.uploaded_at,
    }));
  },

  async trackEvent(event: CampaignAnalyticsEvent): Promise<void> {
    const supabase = createClient();
    // Increment counter on campaign
    const campaign = await campaignService.getById(event.campaignId);
    if (!campaign) return;

    if (event.eventType === 'scan') {
      await supabase
        .from('campaigns')
        .update({ qr_scans: campaign.qrScans + 1 })
        .eq('id', event.campaignId);
    } else if (event.eventType === 'share_initiated') {
      await supabase
        .from('campaigns')
        .update({ share_initiated: campaign.shareInitiated + 1 })
        .eq('id', event.campaignId);
    }

    // Insert analytics event
    await supabase.from('campaign_analytics').insert({
      campaign_id: event.campaignId,
      event_type: event.eventType,
      device_type: event.deviceType || null,
      browser: event.browser || null,
      country: event.country || null,
      region: event.region || null,
    });
  },

  async getAnalytics(campaignId: string) {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('campaign_analytics')
      .select('*')
      .eq('campaign_id', campaignId)
      .order('created_at', { ascending: false });
    if (error) { console.error('getAnalytics error:', error.message); return []; }
    return data || [];
  },

  generateSlug(name: string): string {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .trim()
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .substring(0, 80);
  },

  getCampaignUrl(slug: string): string {
    return `https://knowourindia.com/c/${slug}`;
  },
};
